import { Link, useLocation } from "react-router-dom"
import { CircleHelp, LogOut, Package, Tablet } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import { ThemeToggle } from "@/components/theme-toggle"
import { cn } from "@/lib/utils"
import logo from "@/assets/logo.png"
import type { SessionUser } from "@/lib/api"

interface AppShellProps {
  user: SessionUser
  children: React.ReactNode
}

const NAV = [
  { to: "/", label: "מכשירים", icon: Tablet },
  { to: "/builds", label: "גרסאות", icon: Package },
  { to: "/help", label: "עזרה", icon: CircleHelp },
]

/** "/" פעיל גם בעמוד מכשיר, אבל לא בכל עמוד אחר (התאמת קידומת הייתה צובעת אותו תמיד). */
function isActive(to: string, pathname: string) {
  if (to === "/") return pathname === "/" || pathname.startsWith("/devices")
  return pathname.startsWith(to)
}

export function AppShell({ user, children }: AppShellProps) {
  const { pathname } = useLocation()

  return (
    <div className="min-h-svh bg-background text-foreground">
      <header className="sticky top-0 z-10 border-b bg-background/85 backdrop-blur">
        <div className="mx-auto max-w-6xl px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-4">
              <Link to="/" className="flex shrink-0 items-center gap-2.5 font-semibold">
                {/* הלוגו כהה על רקע שקוף - בערכה הכהה מציבים אותו על שבב לבן כדי שיישאר קריא */}
                <img
                  src={logo}
                  alt="מזלט"
                  className="h-8 w-auto object-contain sm:h-9 dark:rounded-md dark:bg-white dark:p-1"
                />
                <span className="sr-only">ניהול עמדות מרוחקות</span>
              </Link>
              {/* בטלפון הניווט יורד לשורה נפרדת מתחת, כדי שלא יידחס מול הפרופיל */}
              <nav className="hidden items-center gap-1 sm:flex">
                {NAV.map(({ to, label, icon: Icon }) => (
                  <Button key={to} asChild size="sm" variant={isActive(to, pathname) ? "secondary" : "ghost"}>
                    <Link to={to} className={cn(!isActive(to, pathname) && "text-muted-foreground")}>
                      <Icon className="size-3.5" />
                      {label}
                    </Link>
                  </Button>
                ))}
              </nav>
            </div>

            <div className="flex shrink-0 items-center gap-2 sm:gap-3">
              <ThemeToggle />
              <div className="hidden flex-col items-end text-xs md:flex">
                <span className="font-medium leading-tight">{user.name}</span>
                <span className="text-muted-foreground">{user.email}</span>
              </div>
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  title={`${user.name} · ${user.email}`}
                  className="size-8 rounded-full border"
                  referrerPolicy="no-referrer"
                />
              ) : null}
              <Button asChild variant="outline" size="sm" className="hidden sm:inline-flex">
                <a href="/auth/logout">
                  <LogOut className="size-3.5" />
                  התנתק
                </a>
              </Button>
              <Button asChild variant="outline" size="icon-sm" className="sm:hidden">
                <a href="/auth/logout" aria-label="התנתק" title="התנתק">
                  <LogOut className="size-4" />
                </a>
              </Button>
            </div>
          </div>

          <nav className="mt-2 grid grid-cols-3 gap-1 sm:hidden">
            {NAV.map(({ to, label, icon: Icon }) => (
              <Button
                key={to}
                asChild
                size="sm"
                variant={isActive(to, pathname) ? "secondary" : "ghost"}
                className="w-full"
              >
                <Link to={to} className={cn(!isActive(to, pathname) && "text-muted-foreground")}>
                  <Icon className="size-3.5" />
                  {label}
                </Link>
              </Button>
            ))}
          </nav>
        </div>
        <Separator />
      </header>
      <main className="mx-auto max-w-6xl px-4 py-6">{children}</main>
    </div>
  )
}
